/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// ปรับสเกลภาพในเบราว์เซอร์ (A7 เฟส 1 · 25 ต.ค. 2569) — ไม่เสียเครดิต ไม่กินเซิร์ฟเวอร์
//   ครอบ (crop) รอบจุดโฟกัส · เติมพื้นหลัง (fit) สีขาว/สีเลือก/เบลอจากภาพเดิม · ไม่ยืดภาพให้บิด
//   ขนาดผลลัพธ์: ด้านยาว 1080 / 2048 / เท่าต้นฉบับ · JPG หรือ PNG
import { fetchImageBytes } from './imageDownload';

/* สเกลปลายทางที่ร้านใช้ · กระดาษชุด A สัดส่วนเดียวกันหมด (1:√2) ต่างกันที่ขนาดพิมพ์
   printPx = ด้านยาวเมื่อเลือก "ขนาดพิมพ์": A5/A4 = 300 dpi · A3/โปสเตอร์ = เพดาน 4096 (~250 dpi บน A3 · 29 ต.ค. 2569 ประชุมพี่นกขอ A3/โปสเตอร์) */
export const RATIOS = [
  { key: '1:1', w: 1, h: 1, label: 'โพสต์ / IG' },
  { key: '4:5', w: 4, h: 5, label: 'โพสต์แนวตั้ง' },
  { key: '9:16', w: 9, h: 16, label: 'สตอรี่ / Reels / TikTok' },
  { key: '16:9', w: 16, h: 9, label: 'จอในร้าน / YouTube' },
  { key: '3:4', w: 3, h: 4, label: 'โปสเตอร์' },
  { key: '4:3', w: 4, h: 3, label: 'แนวนอน' },
  { key: '2:3', w: 2, h: 3, label: 'โปสเตอร์ 2:3', printPx: 4096 },
  { key: '3:2', w: 3, h: 2, label: 'โปสเตอร์แนวนอน', printPx: 4096 },
  { key: 'A5 ตั้ง', w: 148, h: 210, label: 'พิมพ์ A5', printPx: 2480 },
  { key: 'A5 นอน', w: 210, h: 148, label: 'พิมพ์ A5', printPx: 2480 },
  { key: 'A4 ตั้ง', w: 210, h: 297, label: 'พิมพ์ A4', printPx: 3508 },
  { key: 'A4 นอน', w: 297, h: 210, label: 'พิมพ์ A4', printPx: 3508 },
  { key: 'A3 ตั้ง', w: 297, h: 420, label: 'พิมพ์ A3', printPx: 4096 },
  { key: 'A3 นอน', w: 420, h: 297, label: 'พิมพ์ A3', printPx: 4096 },
];
export const PRINT_FALLBACK = 2048; // "ขนาดพิมพ์" กับสเกลโซเชียลที่ไม่มีขนาดกระดาษ
export const LONG_SIDES = [
  { key: '1080', value: 1080, label: '1080px (โซเชียล)' },
  { key: '2048', value: 2048, label: '2048px (คมชัด)' },
  { key: 'print', value: -1, label: 'ขนาดพิมพ์ตามกระดาษ' },
  { key: 'orig', value: 0, label: 'เท่าต้นฉบับ (ไม่เกิน 4096px)' },
];
export const MAX_SOURCES = 30;
export const MAX_SIDE = 4096; // Safari จำกัดพื้นที่ canvas ~16.7 ล้านพิกเซล (4096²) · ใหญ่กว่านี้ toBlob คืน null

/* โหลดรูปเป็น <img> ที่วาดลง canvas ได้ไม่ติด taint — ไฟล์ = object URL · URL ของระบบ = ดึงเป็น bytes ก่อน (ผ่าน proxy บน dev) */
export async function loadSource(src) {
  let blob;
  if (src instanceof Blob) blob = src;
  else {
    const { bytes, type } = await fetchImageBytes(src);
    blob = new Blob([bytes], { type: type || 'image/jpeg' });
  }
  const objectUrl = URL.createObjectURL(blob);
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => {
      URL.revokeObjectURL(objectUrl); // ไฟล์ที่เปิดไม่ได้ (HEIC ฯลฯ) ไม่ทิ้ง blob URL ค้าง
      reject(new Error('decode'));
    };
    el.src = objectUrl;
  });
  return { img, blob, objectUrl, width: img.naturalWidth, height: img.naturalHeight };
}

/* ขนาดผลลัพธ์ของสเกลหนึ่ง · longSide 0 = "เท่าต้นฉบับ" (ครอบ = พิกเซลในกรอบจริง ไม่ขยาย · เติมพื้นหลัง = ภาพเดิมไม่ถูกย่อ)
   longSide -1 = "ขนาดพิมพ์" ใช้ printPx ของสเกลนั้น · ทุกแบบไม่เกิน MAX_SIDE */
export const targetSize = (ratio, srcW, srcH, sideIn, mode = 'fit', crop = null) => {
  const longSide = sideIn < 0 ? ratio.printPx || PRINT_FALLBACK : sideIn;
  const r = ratio.w / ratio.h;
  let w;
  let h;
  if (longSide > 0) {
    w = r >= 1 ? longSide : Math.round(longSide * r);
    h = r >= 1 ? Math.round(longSide / r) : longSide;
  } else if (mode === 'crop' && crop) {
    w = crop.w;
    h = crop.h;
  } else if (r >= 1) {
    w = Math.max(srcW, Math.round(srcH * r));
    h = Math.round(w / r);
  } else {
    h = Math.max(srcH, Math.round(srcW / r));
    w = Math.round(h * r);
  }
  const over = Math.max(w, h) / MAX_SIDE;
  if (over > 1) {
    w = Math.round(w / over);
    h = Math.round(h / over);
  }
  return { w: Math.max(1, w), h: Math.max(1, h) };
};

/* กรอบครอบที่ใหญ่ที่สุดตามสัดส่วน จัดกึ่งกลางที่จุดโฟกัส (0–1) แล้วล็อกไม่ให้เลยขอบ */
export const cropRect = (ratio, srcW, srcH, focus) => {
  const r = ratio.w / ratio.h;
  let w = srcW;
  let h = Math.round(srcW / r);
  if (h > srcH) {
    h = srcH;
    w = Math.round(srcH * r);
  }
  const fx = focus && Number.isFinite(focus.x) ? focus.x : 0.5;
  const fy = focus && Number.isFinite(focus.y) ? focus.y : 0.5;
  const x = Math.max(0, Math.min(srcW - w, Math.round(fx * srcW - w / 2)));
  const y = Math.max(0, Math.min(srcH - h, Math.round(fy * srcH - h / 2)));
  return { x, y, w, h };
};

/* เบลอแบบไม่พึ่ง ctx.filter (Safari เก่าไม่มี): ย่อเล็กแล้วขยายกลับ 2 ชั้น */
const drawBlurredCover = (ctx, img, W, H) => {
  const small = document.createElement('canvas');
  const sw = Math.max(8, Math.round(W / 24));
  const sh = Math.max(8, Math.round(H / 24));
  small.width = sw;
  small.height = sh;
  const sc = small.getContext('2d');
  const scale = Math.max(sw / img.naturalWidth, sh / img.naturalHeight);
  const dw = img.naturalWidth * scale;
  const dh = img.naturalHeight * scale;
  sc.drawImage(img, (sw - dw) / 2, (sh - dh) / 2, dw, dh);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(small, 0, 0, W, H);
  small.width = 0; // คืนหน่วยความจำ canvas ทันที (Safari ปล่อยช้า)
  small.height = 0;
  ctx.fillStyle = 'rgba(255,255,255,0.18)'; // ให้พื้นเบลอจางลงเล็กน้อย สินค้าเด่นกว่า
  ctx.fillRect(0, 0, W, H);
};

/**
 * วาดภาพตามสเกล → canvas
 * opts: { mode: 'crop'|'fit', focus: {x,y}, bg: 'white'|'color'|'blur', color: '#fff', longSide: number, flatten: bool (JPG = รองพื้นขาว) }
 */
export function renderScaled(img, ratio, opts) {
  const srcW = img.naturalWidth;
  const srcH = img.naturalHeight;
  const crop = opts.mode === 'crop' ? cropRect(ratio, srcW, srcH, opts.focus) : null;
  const { w: W, h: H } = targetSize(ratio, srcW, srcH, opts.longSide || 0, opts.mode, crop);
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  if (crop) {
    if (opts.flatten) {
      ctx.fillStyle = '#ffffff'; // PNG โปร่งใส (รูปลบพื้นหลัง) บันทึกเป็น JPG จะกลายเป็นพื้นดำ → รองพื้นขาวก่อน
      ctx.fillRect(0, 0, W, H);
    }
    ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, W, H);
    return canvas;
  }
  /* fit: ภาพเดิมอยู่ครบ เติมขอบ */
  if (opts.bg === 'blur') drawBlurredCover(ctx, img, W, H);
  else {
    ctx.fillStyle = opts.bg === 'color' ? opts.color || '#ffffff' : '#ffffff';
    ctx.fillRect(0, 0, W, H);
  }
  const s = Math.min(W / srcW, H / srcH);
  const dw = Math.round(srcW * s);
  const dh = Math.round(srcH * s);
  ctx.drawImage(img, Math.round((W - dw) / 2), Math.round((H - dh) / 2), dw, dh);
  return canvas;
}

export const canvasToBlob = (canvas, format) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        canvas.width = 0; // คืนหน่วยความจำทันที ไม่รอ GC (แปลง 30 รูป × 8 สเกล)
        canvas.height = 0;
        if (b) resolve(b);
        else reject(new Error('toBlob'));
      },
      format === 'png' ? 'image/png' : 'image/jpeg',
      0.92
    );
  });

/* ชื่อไฟล์ผลลัพธ์: <ชื่อเดิมไม่มีนามสกุล>_<สเกล> เช่น poster_9x16 */
export const ratioTag = (key) => key.replace(':', 'x').replace(' ', '_');
export const baseName = (name) => String(name || 'image').replace(/\.[a-z0-9]+$/i, '');

/* blob → data URL (ส่งให้ backend) */
export const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('read'));
    fr.readAsDataURL(blob);
  });

/* รูปต้นทางสำหรับส่งให้ AI: ไฟล์ > 4 MB ย่อเป็น JPEG ด้านยาว 2048 (kie.ai รับ ≤ 5 MB · base64 บวม 33%) */
export async function sourceDataUrl(source) {
  if (
    source.blob &&
    source.blob.size <= 4000000 &&
    /^image\/(jpeg|png|webp)$/i.test(source.blob.type || '')
  ) {
    return blobToDataUrl(source.blob);
  }
  const { img } = source;
  const scale = Math.min(1, 2048 / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(img.naturalWidth * scale));
  c.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = c.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(img, 0, 0, c.width, c.height);
  const blob = await canvasToBlob(c, 'jpg');
  return blobToDataUrl(blob);
}
